/**
 * --------------------------------------------------------------------------
 * Frontend template muenchen.de: slider.js
 * @author Patrick Schlesinger ps@contexlabs.com
 * --------------------------------------------------------------------------
 */

import debug from 'debug';
import Splide from '@splidejs/splide';
import Modal from 'bootstrap/js/dist/modal';
import config from '../config/config';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const log = debug(`${config.debugNamespace}:slider Splide`);

/**
 * Splide - SliderSettings
 */
const DefaultSettings = {
  'm-slider-image': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '32px',
    type: 'slide',
    perPage: 2,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '12px',
        center: true,
      },
      576: {
        perPage: 2,
        gap: '1.5rem',
      },
      1200: {
        gap: '2rem',
        perPage: 4,
      },
    },
  },
  'm-slider-comment': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '2rem',
    type: 'slide',
    perPage: 3,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '12px',
      },
      576: {
        perPage: 1,
        gap: '1.5rem',
      },
      768: {
        perPage: 2,
        gap: '1.5rem',
      },
      1200: {
        gap: '2rem',
        perPage: 3,
      },
    },
  },
  'm-slider-teaser': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '2rem',
    type: 'slide',
    perPage: 3,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '12px',
        center: true,
      },
      768: {
        perPage: 2,
        gap: '1.5rem',
      },
      1200: {
        gap: '2rem',
        perPage: 3,
      },
    },
  },
  'm-slider-business-hours': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '2rem',
    type: 'slide',
    perPage: 3,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '12px',
      },
      576: {
        perPage: 2,
        gap: '12px',
      },
      768: {
        perPage: 2,
        gap: '1.5rem',
      },
      1200: {
        gap: '2rem',
        perPage: 3,
      },
    },
  },
  'm-slider-date-filter': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '2rem',
    type: 'slide',
    perPage: 3,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 4,
        gap: '1.5rem',
      },
      576: {
        perPage: 4,
        gap: '12px',
      },
      768: {
        perPage: 4,
        gap: '1.5rem',
      },
      1200: {
        gap: '2rem',
        perPage: 6,
      },
    },
  },
  'm-slider-quote': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '2rem',
    type: 'slide',
    perPage: 3,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '12px',
      },
      576: {
        perPage: 1,
        gap: '1.5rem',
      },
      768: {
        perPage: 1,
        gap: '1.5rem',
      },
      1200: {
        gap: '2rem',
        perPage: 1,
      },
    },
  },
  'm-slider-contained-business': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '0',
    type: 'slide',
    perPage: 1,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '12px',
      },
      576: {
        perPage: 1,
        gap: '1.5rem',
      },
      768: {
        perPage: 2,
        gap: '1.5rem',
      },
      1200: {
        perPage: 3,
        gap: '2rem',
      },
    },
  },
  'm-slider-modal': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '0',
    type: 'slide',
    perPage: 1,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '1.5rem',
      },
      768: {
        perPage: 1,
        gap: '1.5rem',
      },
      1200: {
        perPage: 1,
        gap: '2rem',
      },
    },
  },
  'm-intro-carousel': {
    autoplay: false,
    keyboard: false,
    slideFocus: false,
    lazyLoad: false,
    arrows: false,
    perMove: 1,
    gap: '0',
    type: 'loop',
    perPage: 1,
    pagination: false,
    speed: 350,
    mediaQuery: 'min',
    breakpoints: {
      1: {
        perPage: 1,
        gap: '12px',
      },
      768: {
        perPage: 1,
        gap: '1.5rem',
      },
      1200: {
        perPage: 1,
        gap: '2rem',
      },
    },
  },
};

/**
 * Sliders that exist in modals.
 * @type {string[]}
 */
const isSliderInModal = [
  'm-slider-modal',
];

const DATA_KEY = 'm-slider-splide';
const DATA_ATTR_SLIDER = `data-${DATA_KEY}`;
const DATA_SLIDER_IMAGE_MODAL = 'data-m-slider-image-modal';

const SELECTOR_SLIDER = `[data-${DATA_KEY}]`;

const CLASS_SLIDER_TITLE = 'm-component__title';
const CLASS_SLIDER_BUTTON_PREVIOUS = 'previous-button';
const CLASS_SLIDER_BUTTON_NEXT = 'next-button';
const CLASS_NAME_MODAL_BODY = 'modal';
const CLASS_SLIDER_WRAPPER = 'splide';
const CLASS_SLIDER_ITEM = 'splide__slide';
const CLASS_ACCESSIBILITY_LABEL = 'm-accessibility-label';
const CLASS_HIDDEN = 'is-hidden';

/*
 * ------------------------------------------------------------------------
 * Templates
 * ------------------------------------------------------------------------
 */

/**
 * tplModalSliderCredits
 * @param credits
 * @returns {string}
 */
function tplModalSliderCredits(credits) {
  return `<div class="m-media-image__credits">
    <svg aria-hidden="true" class="icon">
      <use xlink:href="#icon-camera"></use>
    </svg>
    ${credits}
  </div>`;
}

/**
 * tplModalSliderCaption
 * @param caption
 * @returns {string}
 */
function tplModalSliderCaption(caption) {
  return `<figcaption class="m-media-image__caption">${caption}</figcaption>`;
}

/**
 * tplModalSliderItem
 * @param data
 * @returns {string}
 */
function tplModalSliderItem(data) {
  let tplCredits = '';
  if (data.sliderModalCredits !== null && data.sliderModalCredits !== '') {
    tplCredits = tplModalSliderCredits(data.sliderModalCredits);
  }
  let tplFigcaption = '';
  if (data.sliderModalCaption !== null && data.sliderModalCaption !== '') {
    tplFigcaption = tplModalSliderCaption(data.sliderModalCaption);
  }
  return `<div class="splide__slide" role="group" aria-label="${data.sliderModalAriaLabel}">
     <figure class="m-media-image">
        <div class="m-media-image__image">
         <img       class="mde-lazyload"
                    sizes="(max-width: 1080px) 100vw, 1080px"
                    src="${data.sliderModalSrc}"
                    data-lazy-src="${data.sliderModalLazySrc}"
                    data-lazy-srcset="${data.sliderModalSrcset}"
                    alt="${data.sliderModalAlt}">
            ${tplCredits}
        </div>
      ${tplFigcaption}
    </figure>
  </div>`;
}

function tplModalSliderModal(id, arrItems, title = '', accessibilityLabel = '') {
  return `<div class="modal-dialog modal-fullscreen">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title visually-hidden" id="exampleModalLabel">${title}</h5>
        <button type="button" class="modal-button-close" data-bs-dismiss="modal" aria-label="Close">
            <svg aria-hidden="true" class="icon">
                <use xlink:href="#icon-close"></use>
            </svg>
        </button>
      </div>
      <div class="modal-body">
          <div class="container">
              <div class="m-modal-slider">
                  <div class="m-modal-slider__row">
                      <div class="m-modal-slider__column">
                          <section class="m-slider m-slider-image" aria-label="Bildergalerie Slider" data-m-slider-splide="m-slider-modal">
                            <p class="visually-hidden">${accessibilityLabel}</p>
                            <button class="previous-button is-control">
                                <svg class="icon">
                                    <use xlink:href="#icon-arrow-left"></use>
                                </svg>
                                <span class="visually-hidden">Vorheriger Slide</span>
                            </button>
                            <div class="splide">
                              <div class="splide__track">
                                <div class="splide__list">
                                  ${arrItems.map(tplModalSliderItem)
    .join('')}
                                </div>
                              </div>
                            </div>
                            <button class="next-button is-control">
                                <svg class="icon">
                                    <use xlink:href="#icon-arrow-right"></use>
                                </svg>
                                <span class="visually-hidden">Nächster Slide</span>
                            </button>
                          </section>
                      </div>
                  </div>
              </div>
          </div>
      </div>
    </div>
  </div>`;
}


/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Slider {
  constructor(element) {
    if (!element) {
      log('missing element');
      return;
    }

    /**
     * elementWrapper
     * @type {Element}
     */
    this.elementWrapper = element;

    /**
     * objSplide
     * @type {Splide}
     */
    this.objSplide = null;

    /**
     * elementModalSlider
     * @type {Element}
     */
    this.elementModalSlider = null;

    /**
     * elementModalSlider
     * @type {Splide}
     */
    this.objSplideModal = null;

    /**
     * strSliderConfig
     * @type {string}
     */
    this.strSliderConfigKey = this.elementWrapper.getAttribute(DATA_ATTR_SLIDER);
    log(`Init: ${this.strSliderConfigKey}`);

    /**
     * isSliderInModal
     * @type {boolean}
     */
    this.isSliderInModal = false;

    if (isSliderInModal.includes(this.strSliderConfigKey)) {
      this.isSliderInModal = true;
    }

    /**
     * isSliderInModal
     * @type {boolean}
     */
    this.isModalSlider = false;

    const tpmModalSlider = this.elementWrapper.getAttribute(DATA_SLIDER_IMAGE_MODAL);

    if (tpmModalSlider && tpmModalSlider === '1') {
      log('isModalSlider');
      this.isModalSlider = true;
    }

    /**
     * _boolModalIsReady
     * @type {boolean}
     * @private
     */
    this._boolModalIsReady = false;

    /**
     * _arrModalSliderItems
     * @type {*[]}
     * @private
     */
    this._arrModalSliderItems = [];

    /**
     * elementSliderWraper
     * @type {Element}
     */
    this.elementSliderWraper = this.elementWrapper.querySelector(`.${CLASS_SLIDER_WRAPPER}`);

    /**
     * elementSliderButtonPrevious
     * @type {Element}
     */
    this.elementSliderButtonPrevious = this.elementWrapper.querySelector(`.${CLASS_SLIDER_BUTTON_PREVIOUS}`);

    /**
     * elementSliderButtonNext
     * @type {Element}
     */
    this.elementSliderButtonNext = this.elementWrapper.querySelector(`.${CLASS_SLIDER_BUTTON_NEXT}`);

    /**
     * elemntCurrentClickFocusTrace
     * @type {null}
     */
    this.elemntCurrentClickFocusTrace = null;

    this._initSlider(this.strSliderConfigKey);
  }

  /**
   * _initSlider
   */
  _initSlider() {
    let options;
    if (DefaultSettings[this.strSliderConfigKey]) {
      options = DefaultSettings[this.strSliderConfigKey];
    } else {
      log('optionsKey not found', this.strSliderConfigKey, this.elementWrapper);
    }

    if (this.isModalSlider) {
      this._initSliderModal();
    }

    if (options.type === 'loop') {
      // Fallback in Carusel when only one item is available.
      const elementsItems = this.elementSliderWraper.getElementsByClassName(CLASS_SLIDER_ITEM);

      if (elementsItems.length === 1) {
        options.type = 'slide';
      }
    }

    this.objSplide = new Splide(this.elementSliderWraper, options);

    this.objSplide.on('mounted', () => {
      // call lazyLoad.update();
      this._lazyLoadUpdate();
    });

    this.objSplide.mount();

    if (this.elementSliderButtonPrevious) {
      this.elementSliderButtonPrevious.addEventListener('click', () => this._go('<'));
      log('this.objSplide.options.type', this.objSplide.options.type);
      if (this.objSplide.options.type === 'slide') {
        this.elementSliderButtonPrevious.classList.add(CLASS_HIDDEN);
      }
    }

    if (this.elementSliderButtonNext) {
      this.elementSliderButtonNext.addEventListener('click', () => this._go('>'));
      if (this.objSplide.length <= this.objSplide.options.perPage && this.objSplide.options.type === 'slide') {
        this.elementSliderButtonNext.classList.add(CLASS_HIDDEN);
      }
    }

    this.objSplide.on('moved', (e) => this._eventListenersMove(e));

    if (this.isSliderInModal) {
      log('add _addEventListenersModal');
      this._addEventListenersModal();
    }
  }

  /**
   * _initSliderModal
   */
  _initSliderModal() {
    log('_initSliderModal');
    const elementsItems = this.elementSliderWraper.getElementsByClassName(CLASS_SLIDER_ITEM);

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < elementsItems.length; i++) {
      const img = elementsItems[i].getElementsByTagName('img')[0];
      this._arrModalSliderItems[i] = {
        sliderModalAriaLabel: elementsItems[i].getAttribute('aria-label'),
        sliderModalSrc: img.getAttribute('data-sliderModalSrc'),
        sliderModalLazySrc: img.getAttribute('data-sliderModalLazySrc'),
        sliderModalSrcset: img.getAttribute('data-sliderModalSrcset'),
        sliderModalCaption: img.getAttribute('data-sliderModalCaption'),
        sliderModalCredits: img.getAttribute('data-sliderModalCredits'),
        sliderModalAlt: img.getAttribute('data-sliderModalAlt'),
      };

      const ahref = elementsItems[i].getElementsByTagName('a')[0];
      ahref.addEventListener('click', (e) => {
        e.preventDefault();
        this.elemntCurrentClickFocus = e.currentTarget;
        this._mainSliderClick(i);
      });
    }
  }

  /**
   * _openModal
   * @private
   */
  _openModalSlider(index = 0) {
    log('_openModalSlider', index);
    if (this._boolModalIsReady === false) {
      this._initMainSliderModal(index);
    } else {
      this.elemntBootstrapModal.show();
      this.objSplideModal.go(index);
    }
  }

  /**
   * _mainSliderClick
   * @param index
   * @private
   */
  _mainSliderClick(i) {
    this._openModalSlider(i);
  }

  /**
   * _randElmentId
   * @returns {string}
   * @private
   */
  _randElmentId() {
    return Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .substr(2, 10);
  }

  /**
   * _initMainSliderModal
   * @param index
   * @private
   */
  _initMainSliderModal(index = 0) {
    const modalId = this._randElmentId();
    log('initMainSliderModal', modalId);
    this.elementModalTitle = this.elementWrapper.querySelector(`.${CLASS_SLIDER_TITLE}`);
    const elementModalAccessibilityLabel = this.elementWrapper.querySelector(`.${CLASS_ACCESSIBILITY_LABEL}`);
    let strModalAccessibilityLabel = '';
    if (elementModalAccessibilityLabel) {
      strModalAccessibilityLabel = elementModalAccessibilityLabel.innerHTML;
    }
    let strModalTitle = '';
    if (this.elementModalTitle !== null) {
      strModalTitle = this.elementModalTitle.innerHTML;
    }
    const tplModal = document.createElement('div');
    tplModal.setAttribute('tabIndex', '-1');
    tplModal.setAttribute('aria-labelledby', 'exampleModalLabel');
    tplModal.setAttribute('aria-hidden', 'true');
    tplModal.setAttribute('id', modalId);
    tplModal.classList.add('modal');
    tplModal.classList.add('fade');

    tplModal.innerHTML = tplModalSliderModal(modalId,
      this._arrModalSliderItems,
      strModalTitle,
      strModalAccessibilityLabel);

    tplModal.addEventListener('hidden.bs.modal', () => {
      if (this.elemntCurrentClickFocus !== null) {
        this.elemntCurrentClickFocus.focus();
      }
    });

    this.elemntBootstrapModal = new Modal(tplModal, { focus: true });
    document.body.append(tplModal);
    this.elemntBootstrapModal.show();
    // Modal Silder
    this.elementModalSlider = tplModal.querySelectorAll(SELECTOR_SLIDER);

    const classSlider = new Slider(this.elementModalSlider[0]);
    this.objSplideModal = classSlider.getSlider();
    this.objSplideModal.go(index);
    this._boolModalIsReady = true;
    this._lazyLoadUpdate();
  }

  /**
   * getSlider
   * @returns {Splide}
   */
  getSlider() {
    return this.objSplide;
  }

  /**
   * _go
   * @param control {number | string}
   * @private
   */
  _go(control) {
    log(`_go: ${control}`);
    this.objSplide.go(control);
  }

  /**
   * refresh all tny sliders
   * @private
   */
  _rebuild() {
    log('refresh');
    this.objSplide.refresh();
  }

  /**
   * _eventListenersMove
   * @param e
   * @private
   */
  _eventListenersMove(e) {
    if (this.objSplide.options.type === 'slide') {
      this._renderControl(e);
    }
    this._lazyLoadUpdate();
  }

  /**
   * _renderControl
   * @param currentPage
   * @private
   */
  _renderControl(currentPage) {
    const pages = Math.round(this.objSplide.length - this.objSplide.options.perPage);

    // perMove
    if (currentPage > 0) {
      this.elementSliderButtonPrevious.classList.remove(CLASS_HIDDEN);
    } else {
      this.elementSliderButtonPrevious.classList.add(CLASS_HIDDEN);
    }

    if (this.objSplide.length > this.objSplide.options.perPage) {
      if (currentPage === pages) {
        this.elementSliderButtonNext.classList.add(CLASS_HIDDEN);
      } else {
        this.elementSliderButtonNext.classList.remove(CLASS_HIDDEN);
      }
    }
  }

  /**
   * _lazyLoadUpdate
   * @private
   */
  _lazyLoadUpdate() {
    if (typeof window.mdeLazyLoadInstance !== 'undefined') {
      window.mdeLazyLoadInstance.update();
      log('_lazyLoadUpdate');
      return true;
    }
    return false;
  }

  /**
   * Listening on several events
   * @private
   */
  _addEventListenersModal() {
    log('_addEventListeners');
    [...document.getElementsByClassName(CLASS_NAME_MODAL_BODY)].map(
      // eslint-disable-next-line array-callback-return
      (element) => {
        element.addEventListener('shown.bs.modal', () => this._rebuild());
      },
    );
  }
}

/**
 * ------------------------------------------------------------------------
 * Init
 * ------------------------------------------------------------------------
 */

document.addEventListener('DOMContentLoaded', () => {
  [...document.querySelectorAll(SELECTOR_SLIDER)].forEach(
    (ele) => new Slider(ele),
  );
});
